import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import {
  Copy, Users, Check, X, Clock, Send, ChevronRight, ArrowLeft,
  ClipboardList, Settings, UserMinus, Search, Archive, Video, ExternalLink,
  BookOpen, FileText, MessageSquare, Plus, Sparkles, Target, Pin, RotateCw,
  Link2, MessageCircle, BarChart3, Pencil, Trash2
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/EmptyState';
import { Avatar } from '../components/ui/Avatar';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { formatStudentName } from '../utils/student';
import { normalizeMeetUrl } from '../utils/meet';
import { generateJoinCode } from '../utils/joinCode';
import { groupAssignmentsForTeacher, formatTurkishDueDate, formatDueDateForSave } from '../utils/assignment';
import { UpcomingAssignmentsCard } from '../components/UpcomingAssignmentsCard';
import { getClass3DIconPath, extractIconFromDescription, classInitials, getClassGradient } from '../utils/classIcon';
import { ClassIconPicker } from '../components/ClassIconPicker';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import { EmoteText } from '../components/EmoteText';
import { EmojiPickerButton } from '../components/EmojiPickerButton';
import { ReactionBar } from '../components/ReactionBar';
import type { ClassRow, ClassStudentRow, StudentSnapshotRow, LiveSessionRow, AssignmentRow, ClassCommentRow, ClassMaterialRow } from '../types/sinif';

interface StudentRow extends ClassStudentRow {
  snapshot?: StudentSnapshotRow;
}

type TabType = 'akis' | 'odevler' | 'materyaller' | 'kisiler' | 'raporlar';

export default function ClassDetail() {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useUser();
  const navigate = useNavigate();
  const getSupabase = useSupabaseClient();
  const { showToast } = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const [isRegeneratingCode, setIsRegeneratingCode] = useState(false);

  const [classRow, setClassRow] = useState<ClassRow | null>(null);


  const [teacherName, setTeacherName] = useState('Öğretmen');
  const [activeStudents, setActiveStudents] = useState<StudentRow[]>([]);
  const [pendingStudents, setPendingStudents] = useState<StudentRow[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('akis');

  const [searchQuery, setSearchQuery] = useState('');

  // Sınıf Ayarları Modalı
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editApproval, setEditApproval] = useState(false);
  const [editStudentsCanComment, setEditStudentsCanComment] = useState(true);
  const [editIcon, setEditIcon] = useState<string | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Toplu atama formu
  const [isBulkFormOpen, setIsBulkFormOpen] = useState(false);
  const [bulkType, setBulkType] = useState<'video' | 'odev' | 'test'>('video');
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkDescription, setBulkDescription] = useState('');
  const [bulkResourceRef, setBulkResourceRef] = useState('');
  const [bulkDueDate, setBulkDueDate] = useState('');
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  // Google Meet Canlı Ders State
  const [liveSessions, setLiveSessions] = useState<LiveSessionRow[]>([]);
  const [isMeetModalOpen, setIsMeetModalOpen] = useState(false);
  const [inputMeetUrl, setInputMeetUrl] = useState('');
  const [meetError, setMeetError] = useState<string | null>(null);
  const [isStartingMeet, setIsStartingMeet] = useState(false);
  
  // Sınıf Panosu Duyurusu Oluşturma State
  const [announcementText, setAnnouncementText] = useState('');
  const announcementTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [classComments, setClassComments] = useState<ClassCommentRow[]>([]);

  // Sınıf Materyalleri State
  const [classMaterials, setClassMaterials] = useState<ClassMaterialRow[]>([]);
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [matTitle, setMatTitle] = useState('');
  const [matUrl, setMatUrl] = useState('');
  const [matDesc, setMatDesc] = useState('');
  const [matType, setMatType] = useState('PDF');
  const [isAddingMaterial, setIsAddingMaterial] = useState(false);

  // Geçmiş Ders Notu Düzenleme State
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingNotesText, setEditingNotesText] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Yorum & Duyuru Düzenleme State
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);

  const load = useCallback(async () => {
    if (!classId) return;
    const supabase = await getSupabase();

    // 1. Sınıf verisi
    const { data: classData } = await supabase.from('classes').select('*').eq('id', classId).single();
    if (classData) {
      const row = classData as ClassRow;
      setClassRow(row);
      setEditName(row.name);
      setEditApproval(row.requires_approval);
      setEditStudentsCanComment(row.students_can_comment ?? true);

      // Sınıfı kuran eğitmenin ismini profiles veya Clerk me mevcut verisinden çöz
      if (row.teacher_id) {
        const { data: teacherProf } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', row.teacher_id)
          .maybeSingle();

        const name = teacherProf?.full_name || (row.teacher_id === user?.id ? user.fullName || user.firstName : null);
        if (name) setTeacherName(name);
      }
    }

    // 2. Sınıf öğrencileri
    const { data: memberRows } = await supabase
      .from('class_students')
      .select('*')
      .eq('class_id', classId)
      .in('status', ['aktif', 'bekliyor'])
      .order('joined_at', { ascending: false });

    const members = (memberRows as ClassStudentRow[]) ?? [];
    const studentIds = members.map(m => m.student_id);

    let snapshotsById = new Map<string, StudentSnapshotRow>();
    if (studentIds.length > 0) {
      const [{ data: snapshots }, { data: profiles }] = await Promise.all([
        supabase.from('student_snapshots').select('*').in('student_id', studentIds),
        supabase.from('profiles').select('user_id, full_name').in('user_id', studentIds),
      ]);

      const profileNameMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p.full_name]));

      snapshotsById = new Map(
        (snapshots as StudentSnapshotRow[] ?? []).map(s => {
          const profileName = profileNameMap.get(s.student_id);
          const resolvedName = s.display_name && !s.display_name.startsWith('user_')
            ? s.display_name
            : profileName ?? s.display_name;
          return [s.student_id, { ...s, display_name: formatStudentName(resolvedName) }];
        })
      );

      for (const sid of studentIds) {
        if (!snapshotsById.has(sid)) {
          const pName = profileNameMap.get(sid);
          snapshotsById.set(sid, {
            student_id: sid,
            display_name: formatStudentName(pName, 'Öğrenci'),
            last_active_at: null,
            total_study_ms: 0,
            completed_videos: 0,
            total_videos: 0,
            week_study_ms: 0,
            week_completed_videos: 0,
            active_plan_count: 0,
            subject_breakdown: [],
            updated_at: new Date().toISOString(),
          });
        }
      }
    }

    // 3. Canlı Dersler
    const { data: sessionRows } = await supabase
      .from('live_sessions')
      .select('*')
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    setLiveSessions((sessionRows as LiveSessionRow[]) ?? []);

    // 4. Sınıfa atanmış görevler
    const { data: assignmentRows } = await supabase
      .from('assignments')
      .select('*')
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    setAssignments((assignmentRows as AssignmentRow[]) ?? []);

    // 5. Sınıf panosu yorumları
    const { data: commentData } = await supabase
      .from('class_comments')
      .select('*')
      .eq('class_id', classId)
      .is('assignment_id', null)
      .order('created_at', { ascending: false });
    setClassComments((commentData as ClassCommentRow[]) ?? []);

    // 6. Sınıf materyalleri
    const { data: matData } = await supabase
      .from('class_materials')
      .select('*')
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    setClassMaterials((matData as ClassMaterialRow[]) ?? []);

    const enriched: StudentRow[] = members.map(m => ({ ...m, snapshot: snapshotsById.get(m.student_id) }));
    setActiveStudents(enriched.filter(m => m.status === 'aktif'));
    setPendingStudents(enriched.filter(m => m.status === 'bekliyor'));
    setIsLoading(false);
  }, [classId, getSupabase]);

  useEffect(() => { load(); }, [load]);

  async function handleStartGoogleMeet(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !classId) return;
    setMeetError(null);

    const normalizedUrl = normalizeMeetUrl(inputMeetUrl);
    if (!normalizedUrl) {
      setMeetError('Lütfen geçerli bir Google Meet oda linki veya oda kodu girin.');
      return;
    }

    setIsStartingMeet(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('live_sessions').insert({
        teacher_id: user.id,
        student_id: null,
        class_id: classId,
        meeting_url: normalizedUrl,
        scheduled_at: new Date().toISOString(),
        status: 'planlandi',
      });
      if (error) throw error;

      setIsMeetModalOpen(false);
      setInputMeetUrl('');
      load();
    } catch (err) {
      console.error('Google Meet ders başlatılamadı:', err);
      setMeetError('Canlı ders başlatılırken bir hata oluştu.');
    } finally {
      setIsStartingMeet(false);
    }
  }

  async function updateLiveSessionStatus(sessionId: string, status: 'tamamlandi' | 'iptal') {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('live_sessions').update({ status }).eq('id', sessionId);
      if (error) throw error;
      load();
    } catch (err) {
      console.error('Canlı ders durumu güncellenemedi:', err);
      showToast('Canlı ders durumu güncellenemedi.', 'error');
    }
  }

  async function togglePinAssignment(assignmentId: string, currentPinned?: boolean, allIds?: string[]) {
    try {
      const supabase = await getSupabase();
      const idsToUpdate = allIds && allIds.length > 0 ? allIds : [assignmentId];
      const { error } = await supabase
        .from('assignments')
        .update({ is_pinned: !currentPinned })
        .in('id', idsToUpdate);
      if (error) throw error;
      load();
    } catch (err) {
      console.error('Ödev sabitleme hatası:', err);
      showToast('Ödev sabitlenemedi. Şema güncel olmayabilir.', 'error');
    }
  }

  async function handleBulkAssignSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !classId || !bulkTitle.trim()) return;
    setIsBulkSubmitting(true);
    try {
      const supabase = await getSupabase();
      const insertRows = activeStudents.map(s => ({
        teacher_id: user.id,
        student_id: s.student_id,
        class_id: classId,
        type: bulkType,
        title: bulkTitle.trim(),
        description: bulkDescription.trim() || '',
        resource_ref: bulkResourceRef.trim() || null,
        due_date: formatDueDateForSave(bulkDueDate),
        status: 'devam_ediyor',
      }));

      if (insertRows.length > 0) {
        await supabase.from('assignments').insert(insertRows);
      }

      setBulkTitle('');
      setBulkDescription('');
      setBulkResourceRef('');
      setBulkDueDate('');
      setIsBulkFormOpen(false);
      load();
    } catch (err) {
      console.error('Toplu atama hatası:', err);
    } finally {
      setIsBulkSubmitting(false);
    }
  }

  async function handlePostAnnouncement(e: React.FormEvent) {
    e.preventDefault();
    if (!announcementText.trim() || !user || !classId) return;
    const text = announcementText.trim();
    setAnnouncementText('');

    const optimisticItem: ClassCommentRow = {
      id: Date.now().toString(),
      class_id: classId,
      assignment_id: null,
      author_id: user.id,
      author_name: teacherName,
      author_role: 'ogretmen',
      content: text,
      created_at: new Date().toISOString(),
    };

    setClassComments(prev => [optimisticItem, ...prev]);

    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_comments').insert({
        class_id: classId,
        assignment_id: null,
        author_id: user.id,
        author_name: teacherName,
        author_role: 'ogretmen',
        content: text,
      });
      if (error) throw error;
    } catch (err) {
      console.error('Duyuru gönderme hatası:', err);
      setClassComments(prev => prev.filter(c => c.id !== optimisticItem.id));
      showToast('Duyuru gönderilemedi. Lütfen tekrar deneyin.', 'error');
    }
  }

  async function handleAddMaterial(e: React.FormEvent) {
    e.preventDefault();
    if (!matTitle.trim() || !matUrl.trim() || !user || !classId) return;
    setIsAddingMaterial(true);

    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_materials').insert({
        class_id: classId,
        teacher_id: user.id,
        title: matTitle.trim(),
        description: matDesc.trim(),
        file_url: matUrl.trim(),
        file_type: matType,
        file_size: 'Bağlantı',
      });
      if (error) throw error;

      setMatTitle('');
      setMatUrl('');
      setMatDesc('');
      setIsMaterialModalOpen(false);
      load();
    } catch (err) {
      console.error('Materyal ekleme hatası:', err);
      showToast('Materyal eklenemedi. Lütfen tekrar deneyin.', 'error');
    } finally {
      setIsAddingMaterial(false);
    }
  }

  async function handleDeleteAssignmentGroup(assignmentIds: string[], title: string) {
    if (!window.confirm(`"${title}" başlıklı ödev tüm öğrencilerden silinecek. Emin misiniz?`)) return;
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('assignments').delete().in('id', assignmentIds);
      if (error) throw error;
      showToast('Ödev başarıyla geri çekildi/silindi.', 'success');
      load();
    } catch (err) {
      console.error('Ödev silme hatası:', err);
      showToast('Ödev silinirken bir hata oluştu.', 'error');
    }
  }

  async function handleDeleteComment(commentId: string) {
    if (!window.confirm('Bu duyuru veya yorumu silmek istediğinize emin misiniz?')) return;
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_comments').delete().eq('id', commentId);
      if (error) throw error;
      setClassComments(prev => prev.filter(c => c.id !== commentId));
      showToast('Duyuru/Yorum silindi.', 'success');
    } catch (err) {
      console.error('Duyuru silme hatası:', err);
      showToast('Silinirken bir hata oluştu.', 'error');
    }
  }

  async function handleSaveComment(commentId: string) {
    if (!editingCommentText.trim()) return;
    setIsSavingComment(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('class_comments')
        .update({ content: editingCommentText.trim() })
        .eq('id', commentId);
      if (error) throw error;
      setClassComments(prev => prev.map(c => c.id === commentId ? { ...c, content: editingCommentText.trim() } : c));
      setEditingCommentId(null);
      setEditingCommentText('');
      showToast('Duyuru/Yorum güncellendi.', 'success');
    } catch (err) {
      console.error('Güncelleme hatası:', err);
      showToast('Güncellenirken bir hata oluştu.', 'error');
    } finally {
      setIsSavingComment(false);
    }
  }

  async function handleDeleteMaterial(materialId: string) {
    if (!window.confirm('Bu materyali silmek istediğinize emin misiniz?')) return;
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_materials').delete().eq('id', materialId);
      if (error) throw error;
      setClassMaterials(prev => prev.filter(m => m.id !== materialId));
      showToast('Materyal silindi.', 'success');
    } catch (err) {
      console.error('Materyal silme hatası:', err);
      showToast('Materyal silinirken bir hata oluştu.', 'error');
    }
  }

  async function handleSaveSessionNotes(sessionId: string) {
    if (!classId) return;
    setIsSavingNotes(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('live_sessions')
        .update({ notes: editingNotesText.trim() })
        .eq('id', sessionId);
      if (error) throw error;

      setEditingSessionId(null);
      setEditingNotesText('');
      load();
    } catch (err) {
      console.error('Ders notu kaydetme hatası:', err);
      showToast('Ders notu kaydedilemedi. Lütfen tekrar deneyin.', 'error');
    } finally {
      setIsSavingNotes(false);
    }
  }

  async function saveClassSettings(e: React.FormEvent) {
    e.preventDefault();
    if (!classId || !editName.trim()) return;
    setIsSavingSettings(true);
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('classes')
        .update({
          name: editName.trim(),
          requires_approval: editApproval,
          students_can_comment: editStudentsCanComment,
          icon_key: editIcon,
        })
        .eq('id', classId);
      if (error) throw error;
      setIsSettingsOpen(false);
      load();
    } catch (err) {
      console.error('Ayarlar kaydedilemedi:', err);
      showToast('Ayarlar kaydedilemedi. Lütfen tekrar deneyin.', 'error');
    } finally {
      setIsSavingSettings(false);
    }
  }

  async function archiveClass() {
    if (!classId) return;
    const ok = await confirm({
      title: 'Sınıfı arşivle',
      message: 'Bu sınıfı arşivlemek istediğine emin misin? Öğrenciler artık bu sınıfı göremez.',
      confirmLabel: 'Arşivle',
      danger: true,
    });
    if (!ok) return;
    const supabase = await getSupabase();
    await supabase.from('classes').update({ is_archived: true }).eq('id', classId);
    navigate('/panel');
  }

  async function respondToApplication(studentId: string, approve: boolean) {
    const supabase = await getSupabase();
    if (approve) {
      await supabase
        .from('class_students')
        .update({ status: 'aktif', joined_at: new Date().toISOString() })
        .eq('class_id', classId)
        .eq('student_id', studentId);
    } else {
      await supabase
        .from('class_students')
        .update({ status: 'reddedildi' })
        .eq('class_id', classId)
        .eq('student_id', studentId);
    }
    load();
  }

  async function removeStudent(studentId: string, name: string) {
    const ok = await confirm({
      title: 'Öğrenciyi çıkar',
      message: `${name} isimli öğrenciyi sınıftan çıkarmak istediğine emin misin?`,
      confirmLabel: 'Çıkar',
      danger: true,
    });
    if (!ok) return;
    const supabase = await getSupabase();
    await supabase
      .from('class_students')
      .update({ status: 'cikarildi' })
      .eq('class_id', classId)
      .eq('student_id', studentId);
    load();
  }

  async function regenerateCode() {
    if (!classRow) return;
    const ok = await confirm({
      title: 'Kodu yenile',
      message: 'Eski kod artık çalışmayacak. Sınıfta zaten olan öğrenciler etkilenmez, sadece yeni katılım bu kodla yapılabilir.',
      confirmLabel: 'Kodu Yenile',
    });
    if (!ok) return;
    setIsRegeneratingCode(true);
    const supabase = await getSupabase();
    const newCode = generateJoinCode();
    const { error } = await supabase.from('classes').update({ join_code: newCode }).eq('id', classRow.id);
    if (!error) {
      setClassRow({ ...classRow, join_code: newCode });
      showToast('Sınıf kodu yenilendi.');
    } else {
      showToast('Kod yenilenemedi, tekrar dene.', 'error');
    }
    setIsRegeneratingCode(false);
  }

  function copyCode() {
    if (!classRow) return;
    navigator.clipboard.writeText(classRow.join_code);
    showToast('Sınıf katılım kodu kopyalandı!');
  }

  function copyInviteLink() {
    if (!classRow) return;
    const url = `${window.location.origin}/katil?kod=${encodeURIComponent(classRow.join_code)}`;
    navigator.clipboard.writeText(url);
    showToast('Sınıf davet bağlantısı (URL) kopyalandı!');
  }

  function shareOnWhatsApp() {
    if (!classRow) return;
    const url = `${window.location.origin}/katil?kod=${encodeURIComponent(classRow.join_code)}`;
    const text = `Merhaba! Bitmiyo "${classRow.name}" sınıfımıza katılmak için bu bağlantıya tıklayabilirsiniz:\n${url}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
  }

  function copyMeetLink(url: string) {
    navigator.clipboard.writeText(url);
    showToast('Canlı ders bağlantısı kopyalandı!');
  }

  function formatDuration(ms: number) {
    const totalMinutes = Math.round(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours <= 0) return `${minutes} Dk`;
    return `${hours} Saat ${minutes} Dk`;
  }

  // Filtrelenmiş aktif öğrenciler
  const filteredStudents = activeStudents.filter(s => {
    const name = s.snapshot?.display_name ?? s.student_id;
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (isLoading) {
    return <div className="p-10 text-text-muted text-sm animate-pulse">Sınıf detayları yükleniyor...</div>;
  }
  if (!classRow) {
    return <div className="p-10 text-text-muted text-sm">Sınıf bulunamadı.</div>;
  }

  const groupedAssignments = groupAssignmentsForTeacher(assignments);
  const activeMeet = liveSessions.find(s => s.status === 'planlandi');
  const completedSessions = liveSessions.filter(s => s.status === 'tamamlandi');
  const lastCompletedSession = completedSessions[0];
  const completionRate = assignments.length > 0
    ? Math.round((assignments.filter(a => a.status === 'tamamlandi').length / assignments.length) * 100)
    : null;
  const avgStudyMs = activeStudents.length > 0
    ? activeStudents.reduce((sum, s) => sum + (s.snapshot?.total_study_ms ?? 0), 0) / activeStudents.length
    : 0;
  const memberSince = classRow.created_at
    ? new Date(classRow.created_at).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-7xl mx-auto">
      <Helmet>
        <title>{classRow.name} — Bitmiyo Sınıf</title>
        <meta name="description" content={`${classRow.name} sınıfı ödev, duyuru ve öğrenci yönetimi.`} />
      </Helmet>

      {/* Geri Dönüş Linki */}
      <Link
        to="/panel"
        className="inline-flex items-center gap-1.5 text-text-muted text-sm mb-4 hover:text-accent-600 transition-colors font-semibold"
      >
        <ArrowLeft size={14} /> Sınıflara Dön
      </Link>

      {/* Figma 2. Pembe/Mor Gradient Büyük Banner */}
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${getClassGradient(classRow.id)} p-6 md:p-10 shadow-2xl text-white mb-6 animate-fade-in-up`}>
        <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-white/10 blur-xl pointer-events-none" />
        <div className="relative flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-white/20 text-white backdrop-blur-md">
                {classRow.requires_approval ? 'Öğretmen Onaylı Sınıf' : 'Serbest Katılım'}
              </span>
              {classRow.is_archived && (
                <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-black/40 text-white">
                  Arşivlenmiş
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mb-2">
              {getClass3DIconPath(classRow) ? (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-white/20 backdrop-blur-md p-2 flex items-center justify-center border border-white/30 shadow-lg shrink-0">
                  <img src={getClass3DIconPath(classRow)!} alt={classRow.name} className="w-full h-full object-contain filter drop-shadow" />
                </div>
              ) : (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center font-black text-white text-xl md:text-2xl border border-white/30 shadow-lg shrink-0">
                  {classInitials(classRow.name)}
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl md:text-5xl font-black tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {classRow.name}
              </h1>
            </div>
            <p className="text-white/80 text-xs md:text-base font-semibold">
              {teacherName}
              {memberSince && <span className="text-white/60"> • {memberSince} tarihinden beri</span>}
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            <Button
              onClick={() => {
                if (classRow) {
                  setEditIcon(classRow.icon_key ?? extractIconFromDescription(classRow.description).iconKey);
                }
                setIsSettingsOpen(true);
              }}
              className="bg-white/15 hover:bg-white/25 text-white border-0 backdrop-blur-md shadow-none"
            >
              <Settings size={15} /> Sınıf Ayarları
            </Button>
          </div>
        </div>

        <div className="relative grid grid-cols-3 gap-2 sm:gap-3 mt-6 pt-5 border-t border-white/15">
          <div className="text-center sm:text-left">
            <p className="text-xl sm:text-2xl font-black" style={{ fontFamily: 'var(--font-heading)' }}>{activeStudents.length}</p>
            <p className="text-[10px] sm:text-xs text-white/70 font-bold uppercase tracking-wide">Öğrenci</p>
          </div>
          <div className="text-center sm:text-left">
            <p className="text-xl sm:text-2xl font-black" style={{ fontFamily: 'var(--font-heading)' }}>{groupedAssignments.length}</p>
            <p className="text-[10px] sm:text-xs text-white/70 font-bold uppercase tracking-wide">Ödev</p>
          </div>
          <div className="text-center sm:text-left">
            <p className="text-xl sm:text-2xl font-black" style={{ fontFamily: 'var(--font-heading)' }}>
              {activeMeet ? 'Canlı' : lastCompletedSession ? new Date(lastCompletedSession.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '—'}
            </p>
            <p className="text-[10px] sm:text-xs text-white/70 font-bold uppercase tracking-wide">Son Ders</p>
          </div>
        </div>
      </div>

      {/* Mobilde sınıf kodu & davet bağlantısı */}
      <Card className="lg:hidden p-3.5 mb-4 flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-text-muted font-bold shrink-0">Sınıf Kodu:</span>
            <span className="font-mono font-black text-sm text-accent-600 bg-accent-500/10 px-2 py-0.5 rounded-lg tracking-wider truncate">
              {classRow.join_code}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={copyCode} className="p-1.5 rounded-lg text-text-muted hover:text-accent-600 transition-colors" title="Kodu Kopyala">
              <Copy size={15} />
            </button>
            <button onClick={copyInviteLink} className="p-1.5 rounded-lg text-text-muted hover:text-accent-600 transition-colors" title="Davet Bağlantısını (URL) Kopyala">
              <Link2 size={15} />
            </button>
            <button
              onClick={regenerateCode}
              disabled={isRegeneratingCode}
              className="p-1.5 rounded-lg text-text-muted hover:text-accent-600 transition-colors disabled:opacity-50"
              title="Kodu Yenile"
            >
              <RotateCw size={15} className={isRegeneratingCode ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <button
            onClick={copyInviteLink}
            className="bitmiyo-btn bitmiyo-btn-primary flex-1 text-xs py-1.5 gap-1.5"
          >
            <Link2 size={14} /> Davet Linkini Kopyala
          </button>
          <button
            onClick={shareOnWhatsApp}
            className="bitmiyo-btn bg-emerald-600 hover:bg-emerald-500 text-white text-xs py-1.5 px-3 gap-1"
            title="WhatsApp ile Paylaş"
          >
            <MessageCircle size={14} /> WhatsApp
          </button>
        </div>
      </Card>

      {/* Figma 2. Görsel: Sekme Navigasyon Barı (Akış, Ödevler, Materyaller, Kişiler, Raporlar) */}
      <div className="flex items-center justify-between border-b border-border mb-6 gap-2 overflow-x-auto no-scrollbar">
        <nav className="flex items-center gap-1">
          {[
            { id: 'akis', label: 'Akış', icon: MessageSquare },
            { id: 'odevler', label: 'Ödevler', icon: ClipboardList },
            { id: 'materyaller', label: 'Materyaller', icon: BookOpen },
            { id: 'kisiler', label: 'Öğrenciler', icon: Users, badge: pendingStudents.length > 0 ? pendingStudents.length : undefined },
            { id: 'raporlar', label: 'Notlar & Analiz', icon: BarChart3 },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
                  isActive
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400 bg-accent-500/5'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-border/30'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {tab.badge !== undefined && (
                  <span className="ml-1 w-5 h-5 rounded-full bg-warning-500 text-white text-[10px] font-black flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Figma 2. Görsel Layout: Sol Taraf İçerik (70%) + Sağ Taraf Bilgi Paneli (30%) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* SOL KOLON (70%) */}
        <div className="lg:col-span-8 space-y-6">

          {/* TAB 1: AKIŞ (Feed) */}
          {activeTab === 'akis' && (
            <div className="space-y-6">

              {activeMeet ? (
                <Card className="p-5 border-emerald-300 dark:border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-blue-500/5 to-transparent shadow-md">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Video size={28} className="text-emerald-600 dark:text-emerald-400 animate-pulse shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                          <p className="font-black text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                            {activeMeet.student_id ? 'BİREBİR ÖZEL ETÜT ŞU ANDA YAYINDA' : 'CANLI DERS ŞU ANDA YAYINDA'}
                          </p>
                        </div>
                        <p className="text-xs text-text-muted">
                          {activeMeet.student_id ? 'Bu ders etiketlenen öğrenciye özeldir. Diğer sınıf öğrencileri göremez.' : 'Öğrenciler dersinize katılabilir.'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => copyMeetLink(activeMeet.meeting_url)}>
                        <Copy size={14} /> Linki Kopyala
                      </Button>
                      <a
                        href={activeMeet.meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 shadow-sm"
                      >
                        <ExternalLink size={14} /> Odaya Katıl
                      </a>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateLiveSessionStatus(activeMeet.id, 'tamamlandi')}
                        className="text-danger-600 border-danger-200 hover:bg-danger-50"
                      >
                        <X size={14} /> Dersi Bitir
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card className="p-5 border-blue-200 dark:border-blue-500/20 bg-gradient-to-r from-blue-500/5 via-transparent to-transparent">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Video size={26} className="text-blue-600 dark:text-blue-400 shrink-0" />
                      <div>
                        <p className="font-bold text-sm text-text-primary">Google Meet Canlı Ders</p>
                        <p className="text-xs text-text-muted mt-0.5">Tüm sınıfla canlı ders başlatın ve bağlantıyı paylaşın.</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setIsMeetModalOpen(v => !v)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-md shrink-0"
                    >
                      <Video size={16} /> Canlı Ders Başlat
                    </Button>
                  </div>

                  {/* Canlı ders oda hazırlama formu */}
                  {isMeetModalOpen && (
                    <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-500/20 bg-surface/80 p-4 rounded-xl">
                      <p className="text-xs font-black uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-2">
                        <Video size={16} /> Google Meet Odasını Hazırla
                      </p>
                      <form onSubmit={handleStartGoogleMeet} className="flex flex-col gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => window.open('https://meet.new', '_blank')}
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-xs px-3 py-2 rounded-xl transition-all flex items-center gap-1.5"
                          >
                            <ExternalLink size={14} /> 1. Yeni Oda Aç (meet.new)
                          </button>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-text-primary block mb-1">
                            2. Google Meet Oda Linki veya Kodu <span className="text-danger-500">*</span>
                          </label>
                          <input
                            value={inputMeetUrl}
                            onChange={e => setInputMeetUrl(e.target.value)}
                            placeholder="https://meet.google.com/abc-defg-hij veya abc-defg-hij"
                            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-blue-500 transition-colors font-mono"
                          />
                        </div>
                        {meetError && <p className="text-xs text-danger-500 font-semibold">{meetError}</p>}
                        <div className="flex justify-end gap-2 pt-1">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setIsMeetModalOpen(false)}>İptal</Button>
                          <Button type="submit" size="sm" disabled={isStartingMeet || !inputMeetUrl.trim()} className="bg-blue-600 text-white">
                            Dersi Başlat ve Bildir
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </Card>
              )}

              {/* Sınıf Duyuru Oluşturma Kartı */}
              <Card className="p-5 shadow-sm">
                <form onSubmit={handlePostAnnouncement} className="flex gap-3">
                  <Avatar name={teacherName} size={40} />
                  <div className="flex-1">
                    <textarea
                      ref={announcementTextareaRef}
                      value={announcementText}
                      onChange={e => setAnnouncementText(e.target.value)}
                      placeholder="Sınıfınla bir duyuru paylaş veya ödev hakkında bilgi ver..."
                      rows={2}
                      className="w-full rounded-xl border border-border bg-surface p-3 text-sm outline-none focus:border-accent-500 transition-colors resize-none"
                    />
                    <div className="flex justify-end items-center gap-2 mt-2">
                      <EmojiPickerButton textareaRef={announcementTextareaRef} value={announcementText} onChange={setAnnouncementText} />
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!announcementText.trim()}
                        className="bg-accent-600 hover:bg-accent-700 text-white"
                      >
                        <Send size={14} /> Paylaş
                      </Button>
                    </div>
                  </div>
                </form>
              </Card>

              {/* Sabitlenmiş ödevler — her zaman en üstte, ayrı bir bölüm */}
              {groupedAssignments.some(a => a.is_pinned) && (
                <div className="space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary">
                    Sabitlenmiş
                  </h3>
                  {groupedAssignments.filter(a => a.is_pinned).map((a, idx) => (
                    <Card
                      key={a.id}
                      className="p-5 transition-all shadow-sm border-purple-300 dark:border-purple-500/40 bg-gradient-to-r from-purple-500/10 via-pink-500/5 to-transparent animate-fade-in-up"
                      style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <Pin size={22} className="text-purple-600 dark:text-purple-400 fill-current shrink-0" />
                          <div>
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-purple-600 text-white">
                                SABİTLENMİŞ
                              </span>
                              <p className="font-bold text-sm text-text-primary">{a.title}</p>
                              {a.totalStudents > 1 && (
                                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-accent-500/10 text-accent-600 dark:text-accent-400">
                                  {a.completedStudents}/{a.totalStudents} Öğrenci
                                </span>
                              )}
                            </div>
                            {a.due_date && (
                              <p className="text-xs text-text-muted">Son teslim: {new Date(a.due_date).toLocaleDateString('tr-TR')}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => togglePinAssignment(a.id, a.is_pinned, a.allAssignmentIds)}
                            className="p-2 rounded-xl border border-purple-600 bg-purple-600 text-white transition-all hover:scale-105"
                            title="Sabitlemeyi Kaldır"
                          >
                            <Pin size={14} className="fill-current" />
                          </button>
                          <Link
                            to={`/sinif/${classId}/odev/${a.id}`}
                            className="bg-accent-600 hover:bg-accent-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl flex items-center gap-1.5 transition-all shadow-sm hover:shadow-md"
                          >
                            Ödevi Gör <ChevronRight size={14} />
                          </Link>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Zaman tüneli — duyurular + ödevler tek, tarih sıralı akışta */}
              <div className="space-y-4">
                <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary">
                  Son Paylaşımlar
                </h3>

                {groupedAssignments.length === 0 && classComments.length === 0 && liveSessions.filter(s => s.status === 'tamamlandi').length === 0 ? (
                  <EmptyState
                    icon={ClipboardList}
                    title="Henüz paylaşım yok"
                    description="Bir duyuru paylaşın ya da Ödevler sekmesinden yeni bir ödev/test atayın."
                  />
                ) : (
                  [
                    ...groupedAssignments.filter(a => !a.is_pinned).map(a => ({ kind: 'assignment' as const, ts: a.created_at, data: a })),
                    ...classComments.map(c => ({ kind: 'comment' as const, ts: c.created_at, data: c })),
                    ...liveSessions.filter(s => s.status === 'tamamlandi').map(s => ({ kind: 'session' as const, ts: s.created_at, data: s })),
                  ]
                    .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
                    .slice(0, 20)
                    .map((item, idx) => {
                      const delayStyle = { animationDelay: `${Math.min(idx, 10) * 35}ms` };
                      if (item.kind === 'session') {
                        const s = item.data;
                        return (
                          <Card
                            key={`s-${s.id}`}
                            className="p-4 border-blue-200 dark:border-blue-500/20 bg-blue-500/5 animate-fade-in-up"
                            style={delayStyle}
                          >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-3">
                                <Video size={20} className="text-blue-600 dark:text-blue-400 shrink-0" />
                                <div>
                                  <p className="font-bold text-sm text-text-primary">Google Meet Canlı Ders (Tamamlandı)</p>
                                  <p className="text-xs text-text-muted">
                                    Tarih: {new Date(s.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                              </div>

                              <a
                                href={s.meeting_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink size={12} /> Ders Bağlantısı
                              </a>
                            </div>

                            {/* Açıklama / Not Ekleme & Düzenleme */}
                            <div className="mt-3 pt-3 border-t border-border/60">
                              {editingSessionId === s.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editingNotesText}
                                    onChange={e => setEditingNotesText(e.target.value)}
                                    placeholder="Bu derste işlenen konular, ödevler veya ders kaydı bağlantısını yazın..."
                                    rows={2}
                                    className="w-full rounded-xl border border-border bg-surface p-2.5 text-xs outline-none focus:border-blue-500"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => setEditingSessionId(null)}>İptal</Button>
                                    <Button
                                      size="sm"
                                      disabled={isSavingNotes}
                                      onClick={() => handleSaveSessionNotes(s.id)}
                                      className="bg-blue-600 text-white"
                                    >
                                      {isStartingMeet ? 'Başlatılıyor...' : 'Dersi Başlat ve Bildir'}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-xs text-text-secondary italic">
                                    <strong>Ders Notu:</strong> {s.notes || 'Henüz ders notu eklenmedi.'}
                                  </p>
                                  <button
                                    onClick={() => {
                                      setEditingSessionId(s.id);
                                      setEditingNotesText(s.notes || '');
                                    }}
                                    className="text-[11px] font-bold text-accent-600 hover:underline shrink-0"
                                  >
                                    {s.notes ? 'Düzenle' : '+ Not Ekle'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      }
                      return item.kind === 'assignment' ? (
                        <Card key={`a-${item.data.id}`} className="p-5 transition-all shadow-sm hover:border-accent-300 animate-fade-in-up" style={delayStyle}>
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3 min-w-0">
                              <Sparkles size={22} className="text-amber-500 shrink-0" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                  <p className="font-bold text-sm text-text-primary truncate">YENİ ÖDEV: {item.data.title}</p>
                                  {item.data.totalStudents > 1 && (
                                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-accent-500/10 text-accent-600 dark:text-accent-400">
                                      {item.data.completedStudents}/{item.data.totalStudents} Öğrenci
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-text-muted">
                                  {item.data.due_date && <span>Son teslim: {new Date(item.data.due_date).toLocaleDateString('tr-TR')}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => togglePinAssignment(item.data.id, item.data.is_pinned, item.data.allAssignmentIds)}
                                className="p-2 rounded-xl border border-border text-text-muted hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-500/10 transition-all"
                                title="Ödevi En Üstte Sabitle"
                              >
                                <Pin size={14} />
                              </button>
                              <Link
                                to={`/sinif/${classId}/odev/${item.data.id}`}
                                className="bg-accent-600 hover:bg-accent-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl flex items-center gap-1.5 transition-all shadow-sm hover:shadow-md"
                              >
                                Ödevi Gör <ChevronRight size={14} />
                              </Link>
                            </div>
                          </div>
                        </Card>
                      ) : (
                        <Card key={`c-${item.data.id}`} className="p-4 bg-surface border border-border animate-fade-in-up" style={delayStyle}>
                          <div className="flex items-start gap-3">
                            <Avatar name={item.data.author_name} size={36} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-bold text-sm text-text-primary">{item.data.author_name}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-text-muted shrink-0">
                                    {new Date(item.data.created_at).toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  {(user?.id === item.data.author_id || (classRow && user?.id === classRow.teacher_id)) && (
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          setEditingCommentId(item.data.id);
                                          setEditingCommentText(item.data.content);
                                        }}
                                        className="p-1 text-text-muted hover:text-accent-600 rounded transition-colors"
                                        title="Düzenle"
                                      >
                                        <Pencil size={13} />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteComment(item.data.id)}
                                        className="p-1 text-text-muted hover:text-danger-600 rounded transition-colors"
                                        title="Sil"
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {editingCommentId === item.data.id ? (
                                <div className="mt-2 space-y-2">
                                  <textarea
                                    value={editingCommentText}
                                    onChange={e => setEditingCommentText(e.target.value)}
                                    rows={2}
                                    className="w-full rounded-xl border border-border bg-surface p-2.5 text-xs outline-none focus:border-accent-500"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)}>İptal</Button>
                                    <Button
                                      size="sm"
                                      disabled={isSavingComment}
                                      onClick={() => handleSaveComment(item.data.id)}
                                      className="bg-accent-600 text-white"
                                    >
                                      {isSavingComment ? 'Kaydediliyor...' : 'Kaydet'}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap"><EmoteText text={item.data.content} /></p>
                                  <ReactionBar contentType="class_comment" contentId={item.data.id} />
                                </>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })
                )}
              </div>
            </div>
          )}

          {/* TAB 2: ÖDEVLER */}
          {activeTab === 'odevler' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                  <ClipboardList size={16} className="text-accent-600" /> Sınıf Ödevleri ({groupedAssignments.length})
                </h3>
                <Button onClick={() => setIsBulkFormOpen(v => !v)} className="bg-accent-600 text-white w-full sm:w-auto justify-center">
                  <Plus size={16} /> Toplu Ödev / Test Ata
                </Button>
              </div>

              {/* Toplu Atama Formu */}
              {isBulkFormOpen && (
                <Card className="p-5 border-accent-300 dark:border-accent-500/30 bg-accent-500/5">
                  <form onSubmit={handleBulkAssignSubmit} className="space-y-4">
                    <h4 className="font-bold text-sm text-text-primary">Tüm Sınıfa Görev / Ödev Ata</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-bold text-text-secondary block mb-1">Görev Türü</label>
                        <select
                          value={bulkType}
                          onChange={e => setBulkType(e.target.value as any)}
                          className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm"
                        >
                          <option value="video">Video Ders</option>
                          <option value="test">Test Ödevi (soru.bitmiyo.com.tr)</option>
                          <option value="odev">Klasik Ödev</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-text-secondary block mb-1">Başlık</label>
                        <input
                          value={bulkTitle}
                          onChange={e => setBulkTitle(e.target.value)}
                          placeholder="Örn: Türev ve İntegral Testi 1"
                          className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-text-secondary block mb-1">Son Teslim Tarihi</label>
                        <input
                          type="date"
                          value={bulkDueDate}
                          onChange={e => setBulkDueDate(e.target.value)}
                          className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-text-secondary block mb-1">Ödev Yönergesi / Açıklaması (Opsiyonel)</label>
                      <textarea
                        value={bulkDescription}
                        onChange={e => setBulkDescription(e.target.value)}
                        placeholder="Öğrencilerin uygulayacağı detaylı ödev adımlarını ve kurallarını yazın..."
                        rows={2}
                        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-text-secondary block mb-1">Kaynak Bağlantısı / Test Kodu</label>
                      <input
                        value={bulkResourceRef}
                        onChange={e => setBulkResourceRef(e.target.value)}
                        placeholder={bulkType === 'test' ? 'Örn: MAT-TEST-101 veya full URL' : 'https://...'}
                        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm font-mono"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setIsBulkFormOpen(false)}>İptal</Button>
                      <Button type="submit" size="sm" disabled={isBulkSubmitting} className="bg-accent-600 text-white">
                        {isBulkSubmitting ? 'Atanıyor...' : 'Tüm Sınıfa Atamayı Tamamla'}
                      </Button>
                    </div>
                  </form>
                </Card>
              )}

              {groupedAssignments.length === 0 ? (
                <EmptyState
                  icon={ClipboardList}
                  title="Henüz ödev atanmadı"
                  description="Yukarıdaki butonla sınıfa toplu ödev, video veya test atayabilirsiniz."
                />
              ) : (
                <div className="space-y-3">
                  {groupedAssignments.map((a, idx) => (
                    <Card
                      key={a.id}
                      className="p-4 flex items-center justify-between gap-4 flex-wrap animate-fade-in-up"
                      style={{ animationDelay: `${Math.min(idx, 10) * 30}ms` }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {a.type === 'video' ? <Video size={20} className="text-blue-600 shrink-0" /> : a.type === 'test' ? <Target size={20} className="text-accent-600 shrink-0" /> : <FileText size={20} className="text-text-secondary shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-text-primary truncate">{a.title}</p>
                          <p className="text-xs text-text-muted">
                            {a.due_date ? `Son Teslim: ${formatTurkishDueDate(a.due_date)}` : 'Süresiz'}
                            {a.totalStudents > 1 && ` • ${a.totalStudents} Öğrenci`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={a.completedStudents === a.totalStudents && a.totalStudents > 0 ? 'success' : a.completedStudents > 0 ? 'accent' : 'neutral'}>
                          {a.completedStudents === a.totalStudents && a.totalStudents > 0
                            ? 'Tamamlandı'
                            : `${a.completedStudents}/${a.totalStudents} Tamamlandı`}
                        </Badge>
                        <Badge variant={a.type === 'test' ? 'accent' : 'neutral'}>
                          {a.type === 'test' ? 'Test Ödevi' : a.type === 'video' ? 'Video' : 'Ödev'}
                        </Badge>
                        <Link
                          to={`/sinif/${classId}/odev/${a.id}`}
                          className="bg-accent-600 hover:bg-accent-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all shadow-sm hover:shadow-md"
                        >
                          Detay Gör <ChevronRight size={14} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDeleteAssignmentGroup(a.allAssignmentIds, a.title)}
                          className="p-1.5 rounded-lg text-danger-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-950/40 transition-colors"
                          title="Ödevi Geri Çek / Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: MATERYALLER */}
          {activeTab === 'materyaller' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                  <BookOpen size={16} className="text-accent-600" /> Ders Ekleri & Materyaller ({classMaterials.length})
                </h3>
                <Button onClick={() => setIsMaterialModalOpen(v => !v)} className="bg-accent-600 text-white w-full sm:w-auto justify-center">
                  <Plus size={16} /> Yeni Materyal / Bağlantı Ekle
                </Button>
              </div>

              {/* Materyal Ekleme Formu */}
              {isMaterialModalOpen && (
                <Card className="p-5 border-accent-300 dark:border-accent-500/30 bg-accent-500/5">
                  <form onSubmit={handleAddMaterial} className="space-y-4">
                    <h4 className="font-bold text-sm text-text-primary">Yeni Materyal veya Harici Bağlantı Ekle</h4>
                    <p className="text-xs text-text-muted">
                      <strong>Depolama İpucu:</strong> Google Drive, Yandex Disk, YouTube veya PDF linklerini ekleyerek 0 byte depolama kullanımı sağlayabilirsiniz.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-bold text-text-secondary block mb-1">Tür</label>
                        <select
                          value={matType}
                          onChange={e => setMatType(e.target.value)}
                          className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm"
                        >
                          <option value="PDF">PDF Dokümanı</option>
                          <option value="DRIVE">Google Drive / Bulut</option>
                          <option value="LINK">Harici Web Linki</option>
                          <option value="VIDEO">Video Bağlantısı</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-bold text-text-secondary block mb-1">Materyal Başlığı</label>
                        <input
                          value={matTitle}
                          onChange={e => setMatTitle(e.target.value)}
                          placeholder="Örn: Türev Ders Notları 2026.pdf"
                          className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-text-secondary block mb-1">Dosya / Bağlantı URL'si</label>
                      <input
                        value={matUrl}
                        onChange={e => setMatUrl(e.target.value)}
                        placeholder="https://drive.google.com/file/... veya https://..."
                        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm font-mono"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-text-secondary block mb-1">Açıklama (Opsiyonel)</label>
                      <input
                        value={matDesc}
                        onChange={e => setMatDesc(e.target.value)}
                        placeholder="Örn: 3. Hafta işlenen slaytlar ve alıştırma soruları"
                        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setIsMaterialModalOpen(false)}>İptal</Button>
                      <Button type="submit" size="sm" disabled={isAddingMaterial} className="bg-accent-600 text-white">
                        {isAddingMaterial ? 'Ekleniyor...' : 'Materyali Yayınla'}
                      </Button>
                    </div>
                  </form>
                </Card>
              )}

              {classMaterials.length === 0 ? (
                <EmptyState
                  icon={BookOpen}
                  title="Henüz materyal eklenmedi"
                  description="Yukarıdaki butonla ders notları, Google Drive ve doküman bağlantıları paylaşabilirsiniz."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {classMaterials.map((m, idx) => (
                    <Card
                      key={m.id}
                      className="p-4 flex items-center justify-between gap-3 shadow-sm hover:border-accent-300 animate-fade-in-up"
                      style={{ animationDelay: `${Math.min(idx, 10) * 30}ms` }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <BookOpen size={20} className="text-purple-600 dark:text-purple-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Badge variant="accent">{m.file_type}</Badge>
                          </div>
                          <p className="font-bold text-sm text-text-primary truncate">{m.title}</p>
                          <p className="text-xs text-text-muted truncate">{m.description || m.file_size}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a
                          href={m.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-accent-600 hover:bg-accent-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl flex items-center gap-1.5 transition-all hover:shadow-md"
                        >
                          <ExternalLink size={14} /> Aç
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteMaterial(m.id)}
                          className="p-2 text-danger-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-950/40 rounded-xl transition-colors"
                          title="Materyali Sil"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: KİŞİLER (Öğrenciler & Onay Bekleyenler) */}
          {activeTab === 'kisiler' && (
            <div className="space-y-6">
              {/* Bekleyen Başvurular */}
              {classRow.requires_approval && pendingStudents.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-widest text-warning-500 flex items-center gap-2">
                    <Clock size={15} /> Bekleyen Katılım Başvuruları ({pendingStudents.length})
                  </h3>
                  {pendingStudents.map((s, idx) => {
                    const pendingName = formatStudentName(s.snapshot?.display_name, 'Öğrenci');
                    return (
                      <Card
                        key={s.student_id}
                        className="p-4 flex items-center justify-between gap-3 flex-wrap border-warning-200 animate-fade-in-up"
                        style={{ animationDelay: `${Math.min(idx, 10) * 30}ms` }}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar name={pendingName} size={38} />
                          <div>
                            <p className="font-bold text-sm text-text-primary">{pendingName}</p>
                            <span className="text-xs text-warning-500 font-semibold">Onay bekliyor</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => respondToApplication(s.student_id, true)}>
                            <Check size={14} /> Onayla
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => respondToApplication(s.student_id, false)}>
                            <X size={14} /> Reddet
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Aktif Öğrenciler */}
              <div>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                    <Users size={16} className="text-accent-600" /> Kayıtlı Öğrenciler ({filteredStudents.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                      <input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Öğrenci ara..."
                        className="rounded-xl border border-border bg-surface pl-8 pr-3 py-1.5 text-xs outline-none focus:border-accent-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {activeStudents.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="Henüz öğrenci yok"
                    description="Sınıf kodunu paylaşarak öğrencilerin katılmasını sağlayabilirsiniz."
                  />
                ) : filteredStudents.length === 0 ? (
                  <EmptyState
                    icon={Search}
                    title="Sonuç bulunamadı"
                    description={`"${searchQuery}" ile eşleşen bir öğrenci yok.`}
                  />
                ) : (
                  <div className="space-y-2">
                    {filteredStudents.map((s, idx) => {
                      const studentName = formatStudentName(s.snapshot?.display_name, 'Öğrenci');
                      const ratio = s.snapshot && s.snapshot.total_videos > 0
                        ? Math.round((s.snapshot.completed_videos / s.snapshot.total_videos) * 100)
                        : null;
                      return (
                        <Card
                          key={s.student_id}
                          className="p-4 flex items-center justify-between gap-4 hover:border-accent-400 transition-all group animate-fade-in-up"
                          style={{ animationDelay: `${Math.min(idx, 12) * 25}ms` }}
                        >
                          <Link to={`/sinif/${classId}/ogrenci/${s.student_id}`} className="flex items-center gap-4 flex-1 min-w-0">
                            <Avatar name={studentName} size={40} />
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm text-text-primary group-hover:text-accent-600 transition-colors truncate">
                                {studentName}
                              </p>
                              {ratio !== null ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="h-1.5 w-24 rounded-full bg-border overflow-hidden">
                                    <div className="h-full rounded-full bg-accent-500" style={{ width: `${ratio}%` }} />
                                  </div>
                                  <p className="text-xs text-text-muted shrink-0">%{ratio}</p>
                                </div>
                              ) : (
                                <p className="text-xs text-text-muted mt-0.5">Henüz ilerleme verisi yok</p>
                              )}
                            </div>
                          </Link>
                          <div className="flex items-center gap-1 shrink-0">
                            <ChevronRight size={16} className="text-text-muted group-hover:text-accent-600 transition-colors hidden sm:block" />
                            <button
                              onClick={() => removeStudent(s.student_id, studentName)}
                              className="p-2 text-text-muted hover:text-danger-500 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-500/10 transition-colors"
                              title="Sınıftan Çıkar"
                            >
                              <UserMinus size={16} />
                            </button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 5: RAPORLAR / NOTLAR */}
          {activeTab === 'raporlar' && (
            <div className="space-y-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                <Sparkles size={16} className="text-accent-600" /> Sınıf Başarı & İlerleme Özeti
              </h3>

              {activeStudents.length === 0 ? (
                <EmptyState
                  icon={Sparkles}
                  title="Henüz veri yok"
                  description="Sınıfa öğrenci katıldıkça ilerleme özetleri burada görünecek."
                />
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-5 animate-fade-in-up">
                      <p className="font-bold text-sm text-text-primary mb-2">Ortalama Çalışma Süresi</p>
                      <p className="text-3xl font-black text-accent-600" style={{ fontFamily: 'var(--font-heading)' }}>
                        {formatDuration(avgStudyMs)}
                      </p>
                      <p className="text-xs text-text-muted mt-1">Öğrenci başına toplam çalışma</p>
                    </Card>
                    <Card className="p-5 animate-fade-in-up" style={{ animationDelay: '40ms' }}>
                      <p className="font-bold text-sm text-text-primary mb-2">Tamamlanan Ödev Oranı</p>
                      <p className="text-3xl font-black text-emerald-600" style={{ fontFamily: 'var(--font-heading)' }}>
                        {completionRate !== null ? `%${completionRate}` : '—'}
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        {assignments.length > 0
                          ? `${assignments.filter(a => a.status === 'tamamlandi').length} / ${assignments.length} atama tamamlandı`
                          : 'Henüz ödev atanmadı'}
                      </p>
                    </Card>
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-xs font-black uppercase tracking-widest text-text-secondary">Öğrenci Bazında İlerleme</h4>
                    {[...activeStudents]
                      .sort((a, b) => (b.snapshot?.total_study_ms ?? 0) - (a.snapshot?.total_study_ms ?? 0))
                      .map((s, idx) => {
                        const studentName = formatStudentName(s.snapshot?.display_name, 'Öğrenci');
                        const ratio = s.snapshot && s.snapshot.total_videos > 0
                          ? Math.round((s.snapshot.completed_videos / s.snapshot.total_videos) * 100)
                          : null;
                        return (
                          <Card
                            key={s.student_id}
                            as={Link}
                            to={`/sinif/${classId}/ogrenci/${s.student_id}`}
                            className="p-3.5 flex items-center gap-3 hover:border-accent-400 transition-all animate-fade-in-up"
                            style={{ animationDelay: `${Math.min(idx, 12) * 25}ms` }}
                          >
                            <Avatar name={studentName} size={32} />
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-xs text-text-primary truncate">{studentName}</p>
                              <p className="text-[11px] text-text-muted">{formatDuration(s.snapshot?.total_study_ms ?? 0)} çalıştı</p>
                            </div>
                            {ratio !== null && (
                              <div className="flex items-center gap-1.5 shrink-0">
                                <div className="h-1.5 w-16 rounded-full bg-border overflow-hidden hidden sm:block">
                                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${ratio}%` }} />
                                </div>
                                <span className="text-xs font-bold text-accent-600 w-9 text-right">%{ratio}</span>
                              </div>
                            )}
                          </Card>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        {/* SAĞ KOLON (30% - Figma 6. Görsel Sağ Panel) — masaüstünde sabit, mobilde tab içeriğinin altında */}
        <div className="hidden lg:block lg:col-span-4 space-y-6">

          {/* Yaklaşan Ödevler Kartı & Haftalık Takvim */}
          <UpcomingAssignmentsCard
            assignments={assignments}
            liveSessions={liveSessions}
            classId={classId}
            isTeacher={true}
          />

          {/* Figma 6. Görsel: Sınıf Bilgileri Kartı */}
          <Card className="p-6 space-y-4 shadow-sm">
            <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3">
              Sınıf Bilgileri
            </h3>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-text-muted font-medium">Eğitmen:</span>
                <strong className="text-text-primary font-bold">{teacherName}</strong>
              </div>

              {memberSince && (
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-text-muted font-medium">Oluşturulma:</span>
                  <strong className="text-text-primary font-bold">{memberSince}</strong>
                </div>
              )}

              {lastCompletedSession && (
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-text-muted font-medium">Son Canlı Ders:</span>
                  <strong className="text-text-primary font-bold">
                    {new Date(lastCompletedSession.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                  </strong>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-text-muted font-medium">Sınıf Kodu:</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-black text-sm text-accent-600 bg-accent-500/10 px-2 py-0.5 rounded-lg tracking-wider">
                    {classRow.join_code}
                  </span>
                  <button
                    onClick={copyCode}
                    className="p-1 rounded text-text-muted hover:text-accent-600 transition-colors"
                    title="Kodu Kopyala"
                  >
                    <Copy size={13} />
                  </button>
                  <button
                    onClick={copyInviteLink}
                    className="p-1 rounded text-text-muted hover:text-accent-600 transition-colors"
                    title="Davet Bağlantısını (URL) Kopyala"
                  >
                    <Link2 size={13} />
                  </button>
                  <button
                    onClick={regenerateCode}
                    disabled={isRegeneratingCode}
                    className="p-1 rounded text-text-muted hover:text-accent-600 transition-colors disabled:opacity-50"
                    title="Kodu Yenile"
                  >
                    <RotateCw size={13} className={isRegeneratingCode ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Davet Linki & WhatsApp Paylaşımı */}
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <button
                  onClick={copyInviteLink}
                  className="bitmiyo-btn bitmiyo-btn-primary flex-1 text-xs py-1.5 gap-1.5"
                >
                  <Link2 size={13} /> Davet Linkini Kopyala
                </button>
                <button
                  onClick={shareOnWhatsApp}
                  className="bitmiyo-btn bg-emerald-600 hover:bg-emerald-500 text-white text-xs py-1.5 px-2.5 gap-1 shrink-0"
                  title="WhatsApp ile Paylaş"
                >
                  <MessageCircle size={13} /> WhatsApp
                </button>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-text-muted font-medium">Katılım Modu:</span>
                <Badge variant={classRow.requires_approval ? 'warning' : 'accent'}>
                  {classRow.requires_approval ? 'Onaylı' : 'Serbest'}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Figma 6. Görsel: Sınıf İstatistikleri (4 Mini Kart Grid: Öğrenci, Ödev, Materyal, A- Ortalama) */}
          <Card className="p-6 space-y-4 shadow-sm">
            <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3">
              Sınıf İstatistikleri
            </h3>

            <div className="grid grid-cols-2 gap-2.5 text-center">
              <div className="p-3 rounded-2xl bg-surface border border-border">
                <p className="text-2xl font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
                  {activeStudents.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5">Öğrenci</p>
              </div>

              <div className="p-3 rounded-2xl bg-surface border border-border">
                <p className="text-2xl font-black text-accent-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  {groupedAssignments.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5">Ödev</p>
              </div>

              <div className="p-3 rounded-2xl bg-surface border border-border">
                <p className="text-2xl font-black text-purple-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  {classMaterials.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5">Materyal</p>
              </div>

              <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400" style={{ fontFamily: 'var(--font-heading)' }}>
                  %{completionRate ?? 100}
                </p>
                <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">Tamamlanma</p>
              </div>
            </div>
          </Card>

        </div>

      </div>

      {/* Sınıf Ayarları Modalı */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6 relative animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-lg text-text-primary">Sınıf Ayarları</h3>
              <button onClick={() => setIsSettingsOpen(false)} className="p-1 rounded-lg text-text-muted hover:text-text-primary">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={saveClassSettings} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-text-secondary block mb-1">Sınıf Adı</label>
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-accent-500"
                  required
                />
              </div>
              <ClassIconPicker selectedIcon={editIcon} onChange={setEditIcon} />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="modal-edit-approval"
                  checked={editApproval}
                  onChange={e => setEditApproval(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-accent-500"
                />
                <label htmlFor="modal-edit-approval" className="text-xs font-semibold text-text-primary cursor-pointer">
                  Öğrenci katılımları öğretmen onayı gerektirsin
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="modal-edit-comments"
                  checked={editStudentsCanComment}
                  onChange={e => setEditStudentsCanComment(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-accent-500"
                />
                <label htmlFor="modal-edit-comments" className="text-xs font-semibold text-text-primary cursor-pointer">
                  Öğrenciler akışa ve ödevlere yorum/gönderi paylaşabilsin
                </label>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <Button type="button" variant="outline" size="sm" onClick={archiveClass} className="text-danger-500 border-danger-200">
                  <Archive size={14} /> Sınıfı Arşivle
                </Button>
                <Button type="submit" size="sm" disabled={isSavingSettings || !editName.trim()}>
                  {isSavingSettings ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {ConfirmDialog}
    </div>
  );
}
